/**
 * Tracking Utilities for Google Ads and Visitor Tracking
 */

import { trackingAPI } from '../services/apiService';

/**
 * Extract UTM parameters from URL
 * @returns {Object} UTM parameters
 */
export const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    gclid: urlParams.get('gclid'),
  };
};

/**
 * Get referrer source from document.referrer
 * @returns {string} Referrer source
 */
export const getReferrerSource = () => {
  try {
    if (document.referrer) {
      const url = new URL(document.referrer);
      return url.hostname;
    }
    return 'direct';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Check if visitor is from Google Ads
 * @param {Object} utmParams - UTM parameters
 * @returns {boolean} True if from Google Ads
 */
export const isGoogleAdsVisitor = (utmParams) => {
  const { utm_source, utm_medium, gclid } = utmParams;
  return !!(
    gclid ||
    utm_source === 'google_ads' ||
    (utm_source === 'google' && utm_medium === 'cpc')
  );
};

/**
 * Track visitor when they land on a test page
 * @param {string} testType - Type of test (mbti, iq, eq, disc, holland)
 */
export const trackVisitor = async (testType) => {
  try {
    const utmParams = getUTMParams();
    const referrerSource = getReferrerSource();

    await trackingAPI.trackVisitor(testType, referrerSource, utmParams);

    // Store UTM params in sessionStorage for later use
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    sessionStorage.setItem('referrer_source', referrerSource);

    console.log('✅ Visitor tracked:', {
      testType,
      referrerSource,
      utmParams,
      isGoogleAds: isGoogleAdsVisitor(utmParams),
    });
  } catch (error) {
    console.error('❌ Error tracking visitor:', error);
  }
};

/**
 * Track password verification when user successfully enters password
 * @param {string} testType - Type of test (mbti, iq, eq, disc, holland)
 */
export const trackPasswordVerification = async (testType) => {
  try {
    // Get UTM params from sessionStorage (stored when visitor first landed)
    const storedUtmParams = sessionStorage.getItem('utm_params');
    const storedReferrer = sessionStorage.getItem('referrer_source');

    const utmParams = storedUtmParams ? JSON.parse(storedUtmParams) : getUTMParams();
    const referrerSource = storedReferrer || getReferrerSource();

    await trackingAPI.trackPasswordVerification(testType, referrerSource, utmParams);

    console.log('✅ Password verification tracked:', {
      testType,
      referrerSource,
      utmParams,
      isGoogleAds: isGoogleAdsVisitor(utmParams),
    });
  } catch (error) {
    console.error('❌ Error tracking password verification:', error);
  }
};

/**
 * Initialize tracking for a test page
 * Call this in useEffect when component mounts
 * @param {string} testType - Type of test
 */
export const initializeTracking = (testType) => {
  // Track visitor on page load
  trackVisitor(testType);

  // Log UTM params for debugging
  const utmParams = getUTMParams();
  if (Object.values(utmParams).some(v => v)) {
    console.log('🔍 UTM Parameters detected:', utmParams);
  }
};

export default {
  getUTMParams,
  getReferrerSource,
  isGoogleAdsVisitor,
  trackVisitor,
  trackPasswordVerification,
  initializeTracking,
};

